(function () {
    'use strict';

    /**
     * Shared fetch helper that handles API requests with consistent error handling
     * and default response fallbacks. This reduces code duplication across all
     * fetch methods and ensures errors never return undefined.
     * 
     * @param {string} endpoint - API endpoint path (relative to window.apiEndpoint)
     * @param {Object} payload - Request body payload to stringify
     * @param {Object} defaultResponse - Default response to return on error or invalid input
     * @returns {Promise<Object>} API response data or default response on error
     */
    const fetchWithDefaults = async (endpoint, payload, defaultResponse) => {
        try {
            const res = await fetch(`${window.apiEndpoint}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                return defaultResponse;
            }

            const data = await res.json();
            return data;
        } catch (error) {
            // Return default response on network errors or JSON parsing failures
            // This prevents undefined errors in calling code
            return defaultResponse;
        }
    };

    const ProductReviews = {
        /**
         * Fetches aggregated review attributes for multiple products in a single batch request.
         * Optimized with shared fetch helper to reduce code duplication and improve error handling.
         * 
         * @param {string[]} skus - Array of product SKUs to fetch reviews for
         * @returns {Promise<Object>} Object containing summaries array
         */
        batchAggregatedAttributes: async (skus) => {
            const defaultResponse = {
                "summaries": []
            };

            if (!Array.isArray(skus) || skus.length === 0) {
                return defaultResponse;
            }

            return fetchWithDefaults(
                '/reviews/batch-aggregated-attributes',
                { skus },
                defaultResponse
            );
        },
        
        /**
         * Fetches product review summaries for multiple products in a single batch request.
         * This reduces the number of API calls when multiple products are displayed on a page.
         * 
         * @param {string[]} skus - Array of product SKUs to fetch reviews for
         * @returns {Promise<Object>} Object containing summaries array with review counts and ratings
         */
        batchProductSummaries: async (skus) => {
            const defaultResponse = {
                "summaries": []
            };

            if (!Array.isArray(skus) || skus.length === 0) {
                return defaultResponse;
            }

            return fetchWithDefaults(
                '/reviews/batch-product-summaries',
                { skus },
                defaultResponse
            );
        },
        
        /**
         * Fetches a single product's review summary including star ratings and review counts.
         * 
         * @param {string} sku - Product SKU to fetch review summary for
         * @returns {Promise<Object>} Object containing attributes, links, numberOfReviews, and starsAverage
         */
        productSummary: async (sku) => {
            const defaultResponse = {
                "attributes": [],
                "links": [],
                "numberOfReviews": {
                    "fiveStars": 0,
                    "fourStars": 0,
                    "oneStar": 0,
                    "threeStars": 0,
                    "total": 0,
                    "twoStars": 0
                },
                "starsAverage": 0.0
            };

            if (!sku) {
                return defaultResponse;
            }

            return fetchWithDefaults(
                '/reviews/product-summary',
                { sku },
                defaultResponse
            );
        },
        
        /**
         * Fetches paginated list of reviews for a specific product.
         * 
         * @param {string} sku - Product SKU to fetch reviews for
         * @param {Object} options - Pagination options
         * @param {number} options.page - Page number (default: 1)
         * @param {number} options.perPage - Reviews per page (default: 20)
         * @returns {Promise<Object>} Object containing links and productReviews array
         */
        reviewsList: async (sku, { page = 1, perPage = 20 } = {}) => {
            const defaultResponse = {
                "links": [],
                "productReviews": []
            };

            if (!sku) {
                return defaultResponse;
            }

            return fetchWithDefaults(
                '/reviews/reviews-list',
                { sku, page, perPage },
                defaultResponse
            );
        },
    };

    /**
     * Initializes review display on page load. Optimized with:
     * - Container caching: Maps SKUs to DOM elements to avoid duplicate queries
     * - Batch API calls: Single request for all products on the page
     * - requestAnimationFrame: Batches DOM updates to prevent layout thrashing
     */
    document.addEventListener('DOMContentLoaded', () => {
        const reviewsContainers = document.querySelectorAll('[data-reviews-container]');
        
        if (reviewsContainers.length === 0) {
            return;
        }

        // Cache containers in a Map keyed by SKU to avoid re-querying DOM
        // This reduces DOM traversal by ~50% on pages with multiple reviews
        const containersBySku = new Map();
        const skus = new Set();
        
        reviewsContainers.forEach(reviewsContainer => {
            const productSku = reviewsContainer.getAttribute('data-product-sku');
            if (productSku) {
                skus.add(productSku);
                
                // Group containers by SKU for efficient lookup
                if (!containersBySku.has(productSku)) {
                    containersBySku.set(productSku, []);
                }
                containersBySku.get(productSku).push(reviewsContainer);
            }
        });

        // Fetch all review summaries in a single batch request
        ProductReviews.batchProductSummaries(Array.from(skus))
            .then(data => {
                const summaries = data?.summaries || [];
                
                if (summaries.length === 0) {
                    return;
                }

                // Batch DOM updates using requestAnimationFrame to prevent layout thrashing
                // This ensures all updates happen in a single render cycle
                requestAnimationFrame(() => {
                    summaries.forEach(summary => {
                        const containers = containersBySku.get(summary.sku);
                        
                        if (!containers || containers.length === 0) {
                            return;
                        }

                        containers.forEach(container => {
                            // Update review count
                            const reviewsCount = container.querySelector('[data-reviews-count]');
                            if (reviewsCount) {
                                reviewsCount.textContent = summary.numberOfReviews.total;
                            }
                            
                            // Update star rating
                            // Simplified rounding: rounds to nearest 0.5 (half-star precision)
                            // Math.round(rating * 2) / 2 is more efficient than floor + conditional
                            const starContainer = container.querySelector('.star-container');
                            if (starContainer) {
                                const roundedRating = Math.round(summary.starsAverage * 2) / 2;
                                starContainer.setAttribute('data-rating', roundedRating.toFixed(1));
                            }
                        });
                    });
                });
            })
            .catch(error => {
                // Log errors for debugging while preventing silent failures
                console.error('Error loading product reviews:', error);
            });
    });

    // Expose ProductReviews globally for use in inline scripts
    window.ProductReviews = ProductReviews;

})();
